import React, { useState } from 'react';
import { API_URL } from '../config';
import { pdf } from '@react-pdf/renderer';
import { toast } from 'react-toastify';
import { FaFilePdf, FaSpinner } from 'react-icons/fa';
import QuotePDF from './QuotePDF';
import { getCompanyDetails } from '../config/CompanyDetails';

const formatCurrency = (amount) => {
  if (!amount) return 'R 0.00';
  return `R ${parseFloat(amount).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
};

// ============================================
// PROFORMA PDF BUTTON (for the Invoices page)
// Generates the proforma PDF EXACTLY like the quote page does
// (QuotePDF variant="proforma" built from the linked quote + job),
// so the document is identical — no differences between the two.
// ============================================
const ProformaPDFButton = ({ invoice, onSuccess, iconOnly }) => {
  const [busy, setBusy] = useState(false);

  const handleGenerate = async () => {
    if (busy) return;
    const jobId = invoice?.job_id || invoice?.job?.id;
    if (!jobId) {
      toast.error('This proforma is not linked to a job');
      return;
    }
    setBusy(true);
    try {
      // Reuses the same idempotent endpoint the job page uses
      const res = await fetch(`${API_URL}/invoices/proforma/from-job/${jobId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      const data = await res.json();
      if (!data.success) {
        throw new Error(data.error || 'Failed to load proforma');
      }

      const quote = data.quote;
      const jobRecord = data.job;
      const proformaInvoice = data.invoice;

      if (!quote) {
        toast.error('This job has no linked quote, so the proforma PDF cannot be generated.');
        if (onSuccess) onSuccess();
        return;
      }

      const proformaMeta = {
        invoice_number: proformaInvoice.invoice_number || proformaInvoice.proforma_number || 'N/A',
        date: proformaInvoice.invoice_date,
        job_number: jobRecord?.job_number || invoice?.jobs?.job_number || 'N/A',
        pickup_date: jobRecord?.start_date || null
      };

      const company = await getCompanyDetails();

      const blob = await pdf(<QuotePDF quote={quote} variant="proforma" proforma={proformaMeta} companyDetails={company} credit={data.available_credit || null} />).toBlob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Proforma_${proformaMeta.invoice_number}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(url), 1000);

      toast.success(`✅ Proforma ${proformaMeta.invoice_number} downloaded`);
      if (onSuccess) onSuccess();
    } catch (err) {
      console.error('Proforma generate error:', err);
      toast.error(err.message || 'Failed to generate proforma.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      onClick={handleGenerate}
      disabled={busy}
      title="Download Proforma PDF"
      className={iconOnly
        ? "text-gray-400 hover:text-purple-600 transition-colors p-1 bg-transparent shadow-none disabled:opacity-50"
        : "flex items-center gap-2 px-3 py-2 bg-yellow-500 hover:bg-yellow-600 text-white rounded-lg text-xs font-medium disabled:opacity-50"}
    >
      {busy ? <FaSpinner className="animate-spin" size={12} /> : <FaFilePdf size={16} />}
      {!iconOnly && (busy ? 'Generating...' : 'Download Proforma')}
    </button>
  );
};

export default ProformaPDFButton;
